<!-- The app's own action sheet, for the same reason HdDialog exists.
     `app.css` carries an `ion-action-sheet` block written to theme these, and
     on a phone it does nothing. It is not wrong, it is only half true: those
     custom properties and `.action-sheet-button` rules land in Ionic's
     MATERIAL sheet, which is what desktop Chrome renders — so every check
     anyone ever ran on this saw paper and ink. iOS mode builds a different
     element with different parts, and measured there the buttons were
     font-size 20px, font-weight 600, rgb(59,130,246), in the system font.
     Enormous bold blue system text, on a sheet of exercise paper.

     That is the whole menu of this app: "Make a copy", "Send to family",
     "Move to step 3", "Delete this recipe". Every one of them arrives as a
     control from a different product than the page underneath it.

     There is a second thing the stock sheet got wrong, and it is not
     cosmetic. `Delete this recipe` sat flush against `Cancel` with identical
     spacing and identical height — the irreversible action and the escape
     hatch, adjacent, indistinguishable to a thumb. Here the cancel button is
     lifted out of the list entirely and set below it with a gap, which is
     both the iOS convention and the only layout in which a mis-tap between
     those two costs nothing.

     Long lists are the other real case: the note menu offers "Move to step N"
     for every step, so a twelve-step recipe produces fifteen buttons. The
     list scrolls and the cancel button does not move.

     Teleported to <body> like its sibling — inside `ion-content` it would be
     caught in that element's scroll and stacking context — and because it is
     outside `.hd-page`, nothing it inherits can be assumed. Every foreground
     colour below is stated, including the focus ring.

     The prop and button shapes mirror `IonActionSheet` so a call site changes
     by a tag name and one attribute, and a handler may return `false` to keep
     the sheet open, exactly as HdDialog's may. -->
<script setup>
import { ref, computed, watch, nextTick, onBeforeUnmount } from 'vue';

/* One id per instance, for aria-labelledby. A page holds several of these at
   once, and once teleported they are all in <body> together, so the id has to
   be unique per instance rather than per component. Random rather than a
   counter, because everything at the top level of `<script setup>` runs once
   per instance and a module counter declared here would be reset by each. */
const uid = `hd-sheet-${Math.random().toString(36).slice(2, 9)}`;

const props = defineProps({
  /** Open or not. Renders nothing when false. */
  open: { type: Boolean, default: false },
  /** What this menu is about — a recipe's title, a note's first words. Quiet,
      and the sheet's accessible name. */
  header: { type: String, default: '' },
  /** One line under it, for the rare case the header needs qualifying. */
  subHeader: { type: String, default: '' },
  /**
   * [{ text, role?: 'cancel'|'destructive', handler?: () => void }], or a
   * plain string for an entry that only closes the sheet.
   */
  buttons: { type: Array, default: () => [] },
});

/* Both fire on every close path — backdrop, Escape, and any button.
   `update:open` makes `v-model:open` work; `dismiss` is the one-word swap for
   the `@dismiss` the call sites are already written against. Use whichever
   suits; not both on one call site, or the state is set to false twice. */
const emit = defineEmits(['update:open', 'dismiss']);

const panel = ref(null);

const actions = computed(() =>
  props.buttons
    .map((b) => (typeof b === 'string' ? { text: b } : b))
    .filter((b) => b && b.text)
);

/* THE CANCEL BUTTON IS NOT PART OF THE LIST. It is pulled out here rather
   than styled differently in place, because separation is the only thing that
   actually protects the row above it — and the row above it is usually
   "Delete this recipe". The first one wins if a caller ever passes two; the
   rest stay in the list rather than being silently dropped. */
const cancel = computed(() => actions.value.find((b) => b.role === 'cancel'));
const items = computed(() => actions.value.filter((b) => b !== cancel.value));

function close() {
  emit('update:open', false);
  emit('dismiss');
}

function press(button) {
  /* IonActionSheet's `false` contract, kept so a call site can refuse to
     close without having to come and change this file first. */
  const outcome = typeof button.handler === 'function' ? button.handler() : undefined;
  if (outcome === false) return;
  close();
}

/* Backdrop and Escape mean "the cancel button", when there is one — so a
   cancel handler that does cleanup still runs, and so the two gestures cannot
   come to mean something different from the button sitting right there. */
function dismiss() {
  if (cancel.value) press(cancel.value);
  else close();
}

/* Everything this sheet can contain is a button; the rest costs nothing and
   keeps the selector honest if that ever stops being true. */
const FOCUSABLE = 'button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])';

const focusable = () => (panel.value ? Array.from(panel.value.querySelectorAll(FOCUSABLE)) : []);

function onKeydown(event) {
  if (event.key === 'Escape') {
    /* Stopped as well as prevented: Ionic and the router both listen for
       Escape at the document, and one keypress should close one thing. */
    event.preventDefault();
    event.stopPropagation();
    dismiss();
    return;
  }
  if (event.key !== 'Tab') return;

  const list = focusable();
  if (list.length === 0) { event.preventDefault(); return; }

  const first = list[0];
  const last = list[list.length - 1];
  const here = document.activeElement;
  const inside = panel.value?.contains(here);

  if (event.shiftKey && (here === first || !inside)) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && (here === last || !inside)) {
    event.preventDefault();
    first.focus();
  }
}

/* Where focus came from, so it can be put back. Losing it sends a screen
   reader and a keyboard back to the top of the page, which on this app means
   back past the whole recipe to the toolbar. */
let returnTo = null;

function restore() {
  const el = returnTo;
  returnTo = null;
  /* isConnected, because the thing that opened this menu is very often the
     thing the menu just deleted — the note row's own pencil mark. */
  if (el && el.isConnected) el.focus();
}

watch(
  () => props.open,
  (isOpen) => {
    if (!isOpen) { restore(); return; }
    returnTo = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    nextTick(() => {
      /* THE CANCEL BUTTON FIRST, so the first thing Enter can reach is the
         harmless one. This sheet is also how a recipe gets deleted, and the
         delete sits at the bottom of the list it would otherwise land on. */
      const list = focusable();
      const target = list.find((el) => el.dataset.role === 'cancel') ?? list[0];
      target?.focus();
    });
  },
  { immediate: true }
);

/* A sheet unmounted while open — a route change under it — must not leave
   focus on a detached node. */
onBeforeUnmount(() => { if (props.open) restore(); });
</script>

<template>
  <Teleport to="body">
    <Transition name="slide">
      <div
        v-if="open"
        class="veil"
        @click.self="dismiss"
        @keydown="onKeydown"
      >
        <div
          ref="panel"
          class="panel"
          role="dialog"
          aria-modal="true"
          :aria-labelledby="header ? `${uid}-header` : undefined"
          :aria-label="header ? undefined : 'Choose what to do'"
        >
          <div class="list hd-card">
            <div v-if="header || subHeader" class="titles">
              <p v-if="header" :id="`${uid}-header`" class="header">{{ header }}</p>
              <p v-if="subHeader" class="sub">{{ subHeader }}</p>
            </div>

            <!-- The scrolling part, and the only scrolling part: fifteen
                 "Move to step N" entries stay reachable and the cancel button
                 below never leaves the screen. `min-height: 0` in the CSS is
                 what actually lets this shrink inside the panel. -->
            <div class="items">
              <button
                v-for="(button, i) in items"
                :key="`${button.text}-${i}`"
                type="button"
                class="item"
                :class="{ destructive: button.role === 'destructive' }"
                :data-role="button.role || 'default'"
                @click="press(button)"
              >
                {{ button.text }}
              </button>
            </div>
          </div>

          <button
            v-if="cancel"
            type="button"
            class="item cancel hd-card"
            data-role="cancel"
            @click="press(cancel)"
          >
            {{ cancel.text }}
          </button>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
/* 20000 is where Ionic starts its overlays. Sitting exactly on the floor puts
   this above every page, header and footer in the app, and below a toast —
   which is the order you want the one time both are on screen. */
.veil {
  position: fixed;
  inset: 0;
  z-index: 20000;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
  /* The top padding IS the max height: whatever the list cannot fit in what
     is left, it scrolls. A sheet is allowed to be tall, never full-height —
     seeing the page it belongs to is how you know what you are acting on. */
  padding: max(64px, 12vh) 8px max(8px, env(safe-area-inset-bottom, 0px));
  /* 58%, not 45. The gap that separates Cancel from the list is a window onto
     the page behind, and the page behind has a near-black primary action fixed
     to the bottom of it — so at 45% that gap read as a stray dark strip laid
     across the sheet rather than as space. The veil has to be dark enough that
     what shows through it is ground, not an object. */
  background: color-mix(in srgb, var(--hd-ink) 58%, transparent);
}

.panel {
  display: flex;
  flex-direction: column;
  /* The gap between the list and Cancel. It is 8px of nothing, and it is the
     entire reason Delete and Cancel can no longer be confused by a thumb. */
  gap: 8px;
  width: 100%;
  max-width: 420px;
  max-height: 100%;
}

/* 2px, like everything else here. The only rounded thing in this app is the
   24px play disc, and it is round because it is a disc. `.hd-card` gives the
   paper and the hairline; this sets what it cannot know. */
.list {
  display: flex;
  flex-direction: column;
  min-height: 0;
  border-radius: 2px;
  overflow: hidden;
}

/* A LABEL, NOT A SHOUT. This is usually a recipe's title or a note's first
   words, and it is there to say what you are acting on — not to be the
   loudest thing on screen, which is what Ionic's 20px header made of it. */
.titles {
  flex: 0 0 auto;
  padding: 12px 18px 10px;
  border-bottom: 1px solid var(--hd-feint);
}

.header {
  margin: 0;
  font-family: var(--hd-sans);
  font-size: var(--hd-t-figure);
  line-height: 1.35;
  color: var(--hd-pencil);
  /* A note's header is its body text, which can be a paragraph. Two lines of
     it is enough to recognise which note this is. */
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  overflow: hidden;
}

.sub {
  margin: 2px 0 0;
  font-family: var(--hd-serif);
  font-style: italic;
  font-size: var(--hd-t-small);
  line-height: 1.4;
  color: var(--hd-pencil);
}

/* The list shrinks before the panel overflows — `flex: 0 1 auto` with
   `min-height: 0`, rather than a guessed `max-height: 40vh` that would be
   wrong on both a small phone and a tablet. `overscroll-behavior` keeps a
   flick at the end of fifteen steps from scrolling the recipe underneath. */
.items {
  flex: 0 1 auto;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
}

/* 56px, not 44: this is a one-handed list read at arm's length with the other
   hand in a bowl, and every row of it is a whole sentence. The serif at step
   size is the size the recipe's own writing is set in — these are the same
   words as the page, so they are the same words. Not 20px, not semibold, not
   blue, not capitals, not the system font. */
.item {
  display: block;
  width: 100%;
  min-height: 56px;
  box-sizing: border-box;
  margin: 0;
  padding: 14px 18px;
  border: 0;
  border-radius: 2px;
  background: transparent;
  font-family: var(--hd-serif);
  font-size: var(--hd-t-step);
  font-weight: 400;
  text-align: left;
  text-transform: none;
  letter-spacing: normal;
  line-height: 1.4;
  color: var(--hd-ink);
  cursor: pointer;
}

/* A hairline between the rows, in the colour the page's own ruling is drawn
   in — so a menu on a sheet of ruled paper is ruled too. Between, never
   under: a rule below the last row would read as an unreachable sixteenth. */
.items .item + .item {
  border-top: 1px solid var(--hd-feint);
}

/* The margin's red, and only here. Text rather than a fill, because unlike
   HdDialog's confirm this button does not do the deleting — it opens the
   dialog that asks. */
.item.destructive {
  color: var(--hd-margin);
}

/* Its own sheet of paper, below the gap. `.hd-card` in the class list draws
   the hairline and the white inner edge, but its `background` LOSES to the
   `background: transparent` on `.item` above — a scoped rule carries the
   component's own attribute selector and outranks a bare class in app.css.
   Rendered without this line the cancel button is a hole in the sheet with
   the page showing through it. The ink is stated for the usual reason:
   nothing outside `.hd-page` inherits a colour. 600 and centred because this
   one is the way out and should be findable without being read. */
.item.cancel {
  flex: 0 0 auto;
  background: var(--hd-sheet);
  font-family: var(--hd-sans);
  font-size: var(--hd-t-body);
  font-weight: 600;
  text-align: center;
  color: var(--hd-ink);
}

/* A press that answers. Mixed into the paper rather than into `transparent`,
   because the cancel button IS the paper — a translucent tint on it would let
   the backdrop through and darken the page behind instead of the row. No
   hover on the phone this is for; the press is the state that matters. */
.item:active {
  background: color-mix(in srgb, var(--hd-ink) 7%, var(--hd-sheet));
}

@media (hover: hover) {
  .item:hover {
    background: color-mix(in srgb, var(--hd-ink) 4%, var(--hd-sheet));
  }
}

/* Said here rather than inherited: `.hd-page :focus-visible` cannot reach a
   teleported element, and without this the ring is whatever the platform
   draws — which on Android is the blue this component exists to be rid of.
   Inset, because an outline on a full-width row is otherwise clipped by the
   list's own `overflow: hidden`. */
.item:focus-visible {
  outline: 2px solid var(--hd-focus);
  outline-offset: -2px;
}

/* Up from the edge it came from, with the backdrop fading alongside it — a
   backdrop that arrives first reads as two events. The curve is the one a
   sheet wants: quick off the mark, long settle, no overshoot. */
.slide-enter-active,
.slide-leave-active {
  transition: opacity 200ms ease;
}
.slide-enter-active .panel,
.slide-leave-active .panel {
  transition: opacity 200ms ease, transform 240ms cubic-bezier(0.32, 0.72, 0, 1);
}
.slide-enter-from,
.slide-leave-to {
  opacity: 0;
}
.slide-enter-from .panel,
.slide-leave-to .panel {
  opacity: 0;
  /* Its own height plus the safe area, so it starts fully off the bottom
     whether it holds three rows or fifteen. */
  transform: translateY(calc(100% + env(safe-area-inset-bottom, 0px)));
}
/* Still leaving, no longer clickable. */
.slide-leave-active {
  pointer-events: none;
}

/* The travel goes, the crossfade stays — WCAG 2.3.3 asks for the first and
   not the second, and a menu that appears with no transition at all is
   indistinguishable from a mis-tap. */
@media (prefers-reduced-motion: reduce) {
  .slide-enter-from .panel,
  .slide-leave-to .panel {
    transform: none;
  }
}
</style>
